import { SearchFilters } from "../types/providerSearch";
import { SEARCH_CONFIG, hasCareMatchingIntent } from "../constants/providerSearch";
import { normalizeSearchFundingFilters } from "../constants/fundingTaxonomy";
import { getActiveSearchFilterChips } from "./appliedSearchFilterChips";
import { normalizeServiceSelectionsToPanelLabels } from "./serviceMappings";
import { migrateServiceLikeCareTypeToServices } from "./serviceLikeCareTypeMigration";
import {
  normalizeAdultFosterCareFilterLabel,
  migrateEmploymentProviderTypeToCareServices,
  normalizeEmploymentCareServicesForSearch,
} from "./communityProviderTypeMigration";
import { coerceCareTypeFilterLabelToSearchPicklist } from "./providerSearchTaxonomyPayload";
import { canUseAIRecommendations, hasRequiredCareServicesForAiUnlock } from "./aiRecommendationsCriteria";

/**
 * Search age filter naming (Phase 4):
 * - FE / URL / saved-search key: ``age_groups_served`` (product language)
 * - API query param: ``age_brackets`` (storage canon)
 * Mapping is applied in ``providerApi`` / ``publicApi`` and documented in
 * ``apiParameterVerification`` (``age_groups_served`` → ``age_brackets``).
 */

/** Fields that affect search results (excludes display-only geo metadata). */
const SEARCH_COMPARISON_KEYS = [
  "location_query",
  "radius_miles",
  "business_type",
  "min_rating",
  "has_availability",
  "age_groups_served",
  "required_core_services",
  "nice_to_have_core_services",
  "required_service_keys",
  "preferred_service_keys",
  "disability_conditions",
  "accessibility_features",
  "funding_accepted",
  "accepts_medicaid",
  "accepts_medicare",
  "accepts_private_insurance",
  "verified_only",
  "allow_cross_state",
  "languages",
  "other_capacity",
  "sort_by",
  "sort_order",
] as const satisfies readonly (keyof SearchFilters)[];

export const PROVIDER_SEARCH_RESULTS_ELEMENT_ID = "provider-search-results";

export type ProviderSearchMode = "basic" | "advanced" | "recommended";

export type ProviderSearchHandleOptions = {
  scrollToResults?: boolean;
};

function normalizeString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...value]
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean)
    .sort();
}

function normalizedStringArrayField(value: unknown): string[] | undefined {
  const arr = normalizeStringArray(value);
  return arr.length > 0 ? arr : undefined;
}

function normalizeOptionalBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

/**
 * Canonicalize care-service aliases to panel labels at filter ingress points.
 *
 * Includes the Care Type deconflation migration: a service-like `business_type` value
 * (respite_care / wound_care / post_surgery_care / chronic_disease_management — retired
 * from the dropdown per product decision 2026-08-22) is moved into the
 * required-services lane rather than kept as a facility-category filter.
 *
 * Also migrates retired Employment Provider Type → HCBS + required employment keys,
 * and assigns HCBS when employment Care Services are selected without a Provider
 * Type. Adult Foster Care aliases retain their distinct business type.
 * Service strength is always preserved.
 */
export function normalizeSearchFilters(filters: SearchFilters): SearchFilters {
  const deconflated = normalizeEmploymentCareServicesForSearch(
    migrateEmploymentProviderTypeToCareServices(
      normalizeAdultFosterCareFilterLabel(
        migrateServiceLikeCareTypeToServices(filters)
      )
    )
  );
  // Plan 34: density-hidden Home Care Agency (and aliases) → HHA umbrella picklist.
  const coercedBusinessType = coerceCareTypeFilterLabelToSearchPicklist(
    deconflated.business_type
  );
  return {
    ...deconflated,
    business_type: coercedBusinessType || deconflated.business_type,
    required_core_services: normalizeServiceSelectionsToPanelLabels(
      deconflated.required_core_services
    ),
    nice_to_have_core_services: normalizeServiceSelectionsToPanelLabels(
      deconflated.nice_to_have_core_services
    ),
  };
}

/** @alias normalizeSearchFilters */
export const normalizeSearchFilterServiceFields = normalizeSearchFilters;

/** Normalize filters for equality checks and baseline snapshots. */
export function normalizeSearchFiltersForComparison(
  filters: SearchFilters
): SearchFilters {
  const servicesNormalized = normalizeSearchFilters(filters);
  return {
    location_query: normalizeString(servicesNormalized.location_query) || undefined,
    radius_miles:
      servicesNormalized.radius_miles ?? SEARCH_CONFIG.DEFAULT_RADIUS_MILES,
    business_type: servicesNormalized.business_type || undefined,
    min_rating: servicesNormalized.min_rating ?? undefined,
    has_availability: normalizeOptionalBoolean(
      servicesNormalized.has_availability
    ),
    age_groups_served: normalizedStringArrayField(
      servicesNormalized.age_groups_served
    ) as ("18+" | "55+")[] | undefined,
    required_core_services: normalizedStringArrayField(
      servicesNormalized.required_core_services
    ),
    nice_to_have_core_services: normalizedStringArrayField(
      servicesNormalized.nice_to_have_core_services
    ),
    // Community-service canonical keys are slugs; no label normalization needed.
    required_service_keys: normalizedStringArrayField(
      servicesNormalized.required_service_keys
    ),
    preferred_service_keys: normalizedStringArrayField(
      servicesNormalized.preferred_service_keys
    ),
    disability_conditions: normalizedStringArrayField(
      servicesNormalized.disability_conditions
    ),
    accessibility_features: normalizedStringArrayField(
      servicesNormalized.accessibility_features
    ),
    funding_accepted: normalizeSearchFundingFilters(
      servicesNormalized.funding_accepted
    ),
    accepts_medicaid: normalizeOptionalBoolean(servicesNormalized.accepts_medicaid),
    accepts_medicare: normalizeOptionalBoolean(servicesNormalized.accepts_medicare),
    accepts_private_insurance: normalizeOptionalBoolean(
      servicesNormalized.accepts_private_insurance
    ),
    verified_only: servicesNormalized.verified_only === true ? true : undefined,
    allow_cross_state:
      servicesNormalized.allow_cross_state === true ? true : undefined,
    languages: normalizedStringArrayField(servicesNormalized.languages),
    other_capacity:
      normalizeString(servicesNormalized.other_capacity) || undefined,
    sort_by: normalizeString(servicesNormalized.sort_by) || undefined,
    sort_order: servicesNormalized.sort_order || undefined,
  };
}

function arraysEqual(a: unknown, b: unknown): boolean {
  return (
    JSON.stringify(normalizeStringArray(a)) ===
    JSON.stringify(normalizeStringArray(b))
  );
}

export function searchFiltersEqual(
  a: SearchFilters,
  b: SearchFilters
): boolean {
  const left = normalizeSearchFiltersForComparison(a);
  const right = normalizeSearchFiltersForComparison(b);
  return SEARCH_COMPARISON_KEYS.every((key) => {
    const leftValue = left[key];
    const rightValue = right[key];
    if (Array.isArray(leftValue) || Array.isArray(rightValue)) {
      return arraysEqual(leftValue, rightValue);
    }
    return leftValue === rightValue;
  });
}

/** Count active filters for badges and Clear All (location + each removable chip). */
export function countActiveSearchFilters(filters: SearchFilters): number {
  const locationCount = normalizeString(filters.location_query) ? 1 : 0;
  return getActiveSearchFilterChips(filters).length + locationCount;
}

export function determineSearchModeFromFilters(
  filters: SearchFilters,
  explicitMode?: ProviderSearchMode
): ProviderSearchMode {
  if (explicitMode) return explicitMode;

  // Persisted filters.location_query (not live draft) — see aiRecommendationsCriteria.
  if (
    canUseAIRecommendations({
      locationValue: normalizeString(filters.location_query),
      ageGroupsServed: filters.age_groups_served,
      requiredCoreServices: filters.required_core_services,
      requiredServiceKeys: filters.required_service_keys,
    })
  ) {
    return "recommended";
  }
  const hasAgeGroups = (filters.age_groups_served?.length ?? 0) > 0;
  const hasRequiredServices = hasRequiredCareServicesForAiUnlock({
    requiredCoreServices: filters.required_core_services,
    requiredServiceKeys: filters.required_service_keys,
  });
  if (hasAgeGroups || hasRequiredServices) {
    return "advanced";
  }
  return "basic";
}

/**
 * Default mode for the primary Search button and form submit.
 * Never auto-starts AI recommendations — user must opt in explicitly.
 */
export function resolveDefaultSubmitSearchMode(
  filters: SearchFilters,
  explicitMode?: ProviderSearchMode
): ProviderSearchMode {
  if (explicitMode) return explicitMode;
  const inferred = determineSearchModeFromFilters(filters);
  if (inferred === "recommended") {
    return "advanced";
  }
  return inferred;
}

/** Pick search mode when refreshing stale results (respects prior filter-only choice). */
export function resolveSearchModeForRefresh(
  filters: SearchFilters,
  lastMode?: ProviderSearchMode | null
): ProviderSearchMode {
  const inferred = determineSearchModeFromFilters(filters);
  if (lastMode === "advanced" && inferred === "recommended") {
    return "advanced";
  }
  if (lastMode === "recommended" && inferred === "recommended") {
    return "recommended";
  }
  if (lastMode === "basic") {
    return inferred;
  }
  return inferred;
}

export function isProviderSearchMode(
  value: unknown
): value is ProviderSearchMode {
  return value === "basic" || value === "advanced" || value === "recommended";
}

/** True when advanced checkbox panels have selections (expand advanced UI). */
export function shouldAutoExpandAdvancedSections(
  filters: SearchFilters,
  options?: {
    /**
     * When false, payer-class flags do not open More filters (case-linked
     * search keeps Payment accepted as a primary control).
     * @deprecated Age/payer no longer open More filters (viewport: intentional Show).
     */
    includePayerClass?: boolean;
  }
): boolean {
  if (hasCareMatchingIntent(filters)) return true;
  // Age + payer stay under More filters but do NOT auto-open the panel —
  // collapsed chips + intentional "Show" keep Search near the primary row.
  void options?.includePayerClass;
  return Boolean(
    filters.disability_conditions?.length ||
      filters.accessibility_features?.length ||
      filters.funding_accepted?.length ||
      filters.languages?.length ||
      filters.nice_to_have_core_services?.length
  );
}

/**
 * Collapse advanced panels on shortcut navigations (`?auto_search=true`), not `?mode=advanced`.
 *
 * `auto_search` is shared by dashboard quick actions, recent searches, and saved searches:
 * it triggers an automatic search run and, unless `mode=advanced` is set, keeps advanced UI collapsed.
 */
export function shouldDeferShortcutExpandBaseline(
  searchParams: Pick<URLSearchParams, "get">
): boolean {
  return (
    searchParams.get("auto_search") === "true" &&
    searchParams.get("mode") !== "advanced"
  );
}

/** @deprecated Use {@link shouldDeferShortcutExpandBaseline} */
export const shouldSuppressAutoExpandFromFilters = shouldDeferShortcutExpandBaseline;

function sortedStringArray(values?: string[]): string[] {
  return [...(values ?? [])].sort((a, b) => a.localeCompare(b));
}

/** True when care-service sub-panels inside advanced search should be expanded. */
export function shouldExpandCareSubsections(filters: SearchFilters): boolean {
  return (
    hasCareMatchingIntent(filters) ||
    (filters.disability_conditions?.length ?? 0) > 0 ||
    (filters.accessibility_features?.length ?? 0) > 0 ||
    (filters.required_core_services?.length ?? 0) > 0 ||
    (filters.nice_to_have_core_services?.length ?? 0) > 0 ||
    (filters.required_service_keys?.length ?? 0) > 0 ||
    (filters.preferred_service_keys?.length ?? 0) > 0
  );
}

/** True when funding/language sub-panels inside advanced search should be expanded. */
export function shouldExpandFundingLanguageSubsections(
  filters: SearchFilters
): boolean {
  return (
    (filters.funding_accepted?.length ?? 0) > 0 ||
    (filters.languages?.length ?? 0) > 0
  );
}

/** Snapshot of advanced filter fields — detect user edits after shortcut load. */
export function advancedFilterExpandFingerprint(filters: SearchFilters): string {
  const servicesNormalized = normalizeSearchFilters(filters);
  return JSON.stringify({
    age_groups_served: sortedStringArray(servicesNormalized.age_groups_served),
    required_core_services: sortedStringArray(
      servicesNormalized.required_core_services
    ),
    disability_conditions: sortedStringArray(
      servicesNormalized.disability_conditions
    ),
    accessibility_features: sortedStringArray(
      servicesNormalized.accessibility_features
    ),
    funding_accepted: sortedStringArray(servicesNormalized.funding_accepted),
    languages: sortedStringArray(servicesNormalized.languages),
    nice_to_have_core_services: sortedStringArray(
      servicesNormalized.nice_to_have_core_services
    ),
  });
}

/** Snapshot of search-settings fields — detect user edits after shortcut load. */
export function searchSettingsExpandFingerprint(filters: SearchFilters): string {
  return JSON.stringify({
    sort_by: filters.sort_by ?? null,
    sort_order: filters.sort_order ?? null,
    verified_only: filters.verified_only ?? false,
    allow_cross_state: filters.allow_cross_state ?? false,
    min_rating: filters.min_rating ?? null,
  });
}

/**
 * URL params that dashboard/recent/saved shortcuts may set.
 * On `auto_search` navigations, omitted params reset to defaults instead of persisting.
 */
const SHORTCUT_RESETTABLE_PARAMS: ReadonlyArray<{
  param: string;
  filterKey: keyof SearchFilters;
}> = [
  { param: "business_type", filterKey: "business_type" },
  { param: "required_core_services", filterKey: "required_core_services" },
  { param: "nice_to_have_core_services", filterKey: "nice_to_have_core_services" },
  { param: "required_service_keys", filterKey: "required_service_keys" },
  { param: "preferred_service_keys", filterKey: "preferred_service_keys" },
  { param: "radius_miles", filterKey: "radius_miles" },
  { param: "has_availability", filterKey: "has_availability" },
  { param: "accepts_medicaid", filterKey: "accepts_medicaid" },
  { param: "accepts_medicare", filterKey: "accepts_medicare" },
  { param: "accepts_private_insurance", filterKey: "accepts_private_insurance" },
  { param: "age_groups_served", filterKey: "age_groups_served" },
  { param: "disability_conditions", filterKey: "disability_conditions" },
  { param: "accessibility_features", filterKey: "accessibility_features" },
  { param: "funding_accepted", filterKey: "funding_accepted" },
  { param: "languages", filterKey: "languages" },
  { param: "min_rating", filterKey: "min_rating" },
  { param: "verified_only", filterKey: "verified_only" },
  { param: "allow_cross_state", filterKey: "allow_cross_state" },
  { param: "sort_by", filterKey: "sort_by" },
  { param: "sort_order", filterKey: "sort_order" },
  { param: "other_capacity", filterKey: "other_capacity" },
] as const satisfies ReadonlyArray<{
  param: string;
  filterKey: keyof SearchFilters;
}>;

type ShortcutResettableFilterKey =
  (typeof SHORTCUT_RESETTABLE_PARAMS)[number]["filterKey"];

function applyShortcutFilterDefault<K extends ShortcutResettableFilterKey>(
  merged: SearchFilters,
  filterKey: K,
  defaults: SearchFilters
): void {
  merged[filterKey] = defaults[filterKey];
}

function clearStaleCoordinatesForCaseLocationSearch(
  merged: SearchFilters,
  searchParams: Pick<URLSearchParams, "has">
): void {
  const hasCaseId = searchParams.has("case_id");
  const hasLocationText =
    searchParams.has("location_query") || searchParams.has("address");
  const hasExplicitCoords =
    searchParams.has("lat") && searchParams.has("lng");

  if (!hasCaseId || !hasLocationText || hasExplicitCoords) {
    return;
  }

  merged.lat = undefined;
  merged.lng = undefined;
  merged.coordinates = undefined;
  merged.location_type = "address";
}

export function mergeSearchFiltersFromUrl(
  persisted: SearchFilters,
  urlFilters: SearchFilters,
  defaults: SearchFilters,
  searchParams: Pick<URLSearchParams, "has">,
  options: { resetOmittedOnAutoSearch: boolean }
): SearchFilters {
  const merged: SearchFilters = {
    ...defaults,
    ...persisted,
    ...urlFilters,
  };

  clearStaleCoordinatesForCaseLocationSearch(merged, searchParams);

  const withNormalizedServices = normalizeSearchFilters(merged);

  if (!options.resetOmittedOnAutoSearch) {
    return withNormalizedServices;
  }

  for (const { param, filterKey } of SHORTCUT_RESETTABLE_PARAMS) {
    if (!searchParams.has(param)) {
      applyShortcutFilterDefault(withNormalizedServices, filterKey, defaults);
    }
  }

  return normalizeSearchFilters(withNormalizedServices);
}

/** True when sort or display settings filters are active (expand settings panel). */
export function shouldAutoExpandSearchSettings(
  filters: SearchFilters
): boolean {
  return Boolean(
    filters.sort_by ||
      (filters.sort_order &&
        filters.sort_order !== SEARCH_CONFIG.DEFAULT_SORT_ORDER) ||
      filters.verified_only === true ||
      filters.allow_cross_state === true ||
      (typeof filters.min_rating === "number" && filters.min_rating > 0)
  );
}
